import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/Badges";
import LevelBadge from "@/components/LevelBadge";
import { certApi } from "@/lib/api";
import { Search, Loader2, ShieldQuestion } from "lucide-react";

const Verify = () => {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("id") || params.get("q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await certApi.verify(q.trim());
      setResults(data.results);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (params.get("id") || params.get("q")) doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Certificate Verification" title="Verify a WECH-RB certificate"
        subtitle="Search by company name or certificate ID to confirm a certificate's status and scope." />
      <section className="wechr-section">
        <div className="wechr-container max-w-3xl">
          <form onSubmit={doSearch} className="flex flex-col gap-3 sm:flex-row">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Acme Manufacturing or WECH-2026-ABC123" data-testid="certificate-verification-search-input" className="flex-1" />
            <Button type="submit" className="btn-green" disabled={loading} data-testid="certificate-verification-search-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Verify
            </Button>
          </form>

          <div className="mt-8 space-y-4">
            {results === null ? null : results.length === 0 ? (
              <Card><CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                <ShieldQuestion className="h-10 w-10 text-[var(--wechr-muted-text)]" />
                <p className="font-medium text-navy-900">No certificate found</p>
                <p className="text-sm text-[var(--wechr-muted-text)]">Check the company name or certificate ID and try again.</p>
              </CardContent></Card>
            ) : (
              results.map((r) => (
                <Card key={r.certificate_id} data-testid="certificate-verification-result-card">
                  <CardContent className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center">
                    <LevelBadge level={r.level} size={92} showLabel={false} />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-heading text-lg font-semibold text-navy-900">{r.company_name}</h3>
                        <StatusBadge status={r.status} testId="certificate-verification-status-badge" />
                      </div>
                      <p className="font-mono text-sm text-[var(--wechr-muted-text)]">{r.certificate_id}</p>
                      <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                        <div><span className="text-[var(--wechr-muted-text)]">Level</span><div className="font-medium text-navy-900">{r.level} ({r.overall_score}/100)</div></div>
                        <div><span className="text-[var(--wechr-muted-text)]">Reviewer</span><div className="font-medium text-navy-900">{r.reviewer}</div></div>
                        <div><span className="text-[var(--wechr-muted-text)]">Issue date</span><div className="font-medium text-navy-900">{r.issue_date}</div></div>
                        <div><span className="text-[var(--wechr-muted-text)]">Expiration</span><div className="font-medium text-navy-900">{r.expiry_date}</div></div>
                      </div>
                      <p className="mt-3 text-sm text-[var(--wechr-muted-text)]"><span className="font-medium text-navy-900">Scope:</span> {r.scope}</p>
                      <a href={`/certificate/${r.certificate_id}`} className="mt-3 inline-block text-sm font-medium text-wgreen-700 hover:underline">View certificate →</a>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Verify;
